import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { CardBadge } from '@/components/payments/card-badge';
import { CryptoOnchainMethodBadge } from '@/components/payments/crypto-onchain-method-badge';
import { cn } from '@/lib/utils';

/**
 * Single source of truth for "what payment method was used on this payment?"
 * rendering across the CRM (Orders > Payments table, standalone Payments table,
 * Subscriptions > Payments table, …).
 *
 * Why centralize: the answer lives in three places — the instrument row, the
 * payment metadata, and integration breadcrumbs (HiPay, Convesio, custom
 * payments, on-chain crypto). Keeping the resolution logic in one helper
 * avoids the previous drift where the embedded order table fell back to "—"
 * for Apple Pay while the standalone table rendered an "Apple Pay" badge.
 */

// ---------------------------------------------------------------------------
// Public input / output
// ---------------------------------------------------------------------------

export interface PaymentMethodInput {
  paymentInstrument?: {
    type?: string | null;
    card?: {
      brand?: string | null;
      last4?: string | null;
    } | null;
  } | null;
  metadata?: Record<string, unknown> | null;
  /** Set by the payment and order lists on an on-chain crypto payment (no instrument, no processor). */
  cryptoOnchain?: {
    asset: string | null;
    network: string | null;
    isTest: boolean;
  } | null;
}

export type PaymentMethodDisplay =
  | { kind: 'crypto'; asset?: string; network?: string; isTest: boolean }
  | { kind: 'wallet'; wallet: 'apple_pay' | 'google_pay'; label: string; brand?: string; last4?: string }
  | { kind: 'card'; brand?: string; last4?: string }
  | { kind: 'apm'; label: string }
  | { kind: 'integration'; label: string; brand?: string; last4?: string }
  | { kind: 'unknown' };

// ---------------------------------------------------------------------------
// Label maps
// ---------------------------------------------------------------------------

/** APM provider-specific instrument types → friendly label. */
const APM_LABELS: Record<string, string> = {
  apm_airwallex_klarna: 'Klarna',
  apm_airwallex_afterpay: 'Afterpay',
  apm_airwallex_paypal: 'PayPal',
  apm_airwallex_skrill: 'Skrill',
  apm_airwallex_bancontact: 'Bancontact',
  apm_airwallex_ideal: 'iDEAL',
  apm_airwallex_twint: 'TWINT',
  apm_stripe_klarna: 'Klarna',
  apm_stripe_afterpay: 'Afterpay',
  apm_stripe_paypal: 'PayPal',
  apm_stripe_ideal: 'iDEAL',
  apm_stripe_bancontact: 'Bancontact',
  apm_stripe_twint: 'TWINT',
  apm_stripe_revolut_pay: 'Revolut',
  apm_stripe_blik: 'BLIK',
  apm_stripe_affirm: 'Affirm',
};

/** Wallet instrument types that wrap an underlying card. */
const WALLET_LABELS = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
} as const;

export type WalletType = keyof typeof WALLET_LABELS;

/**
 * Instrument types that mean "this wallet", whatever the processor stored.
 * Express checkouts on Stripe-backed processors mint `apm_stripe_apple_pay` /
 * `apm_stripe_google_pay` rows (tokenless, see #TAG-2237) next to the plain
 * `apple_pay` / `google_pay` card-wallet rows; both are the same wallet to a
 * merchant, so both render the wallet badge instead of falling through to the
 * generic `apm_*` prefix strip that produced a lowercase "apple pay" (#TAG-2239).
 */
const WALLET_INSTRUMENT_TYPES: Record<string, WalletType> = {
  apple_pay: 'apple_pay',
  google_pay: 'google_pay',
  apm_stripe_apple_pay: 'apple_pay',
  apm_stripe_google_pay: 'google_pay',
};

/** Resolve an instrument type to the wallet it represents, or `undefined`. */
export function resolveWalletType(type: string | null | undefined): WalletType | undefined {
  return type ? WALLET_INSTRUMENT_TYPES[type] : undefined;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract last4 from a partially-masked PAN like "411111******1111". */
function extractLast4FromPan(pan: string | undefined | null): string | undefined {
  if (!pan || typeof pan !== 'string') return undefined;
  const digits = pan.replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : undefined;
}

/** Some non-Tagada integrations (HiPay, Convesio, custom) keep brand+pan in the
 * payment metadata rather than on payment_instruments.card. Parse that out so
 * we can still render a meaningful badge. */
function getIntegrationDisplay(
  metadata: Record<string, unknown> | null | undefined,
): { brand?: string; last4?: string; label: string } | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const createdBy = metadata.createdBy as string | undefined;
  if (!createdBy?.startsWith('integration:')) return null;

  if (createdBy === 'integration:custom_payment') {
    const snapshot = metadata.manualPaymentSnapshot as { displayName?: string } | undefined;
    return { label: snapshot?.displayName || 'Custom payment' };
  }

  const provider = createdBy.replace('integration:', '');
  const brand = (metadata.hipayBrand as string) ?? (metadata as { convesioBrand?: string }).convesioBrand;
  const pan = (metadata.hipayPan as string) ?? (metadata as { convesioPan?: string }).convesioPan;
  const last4 = extractLast4FromPan(pan);
  return {
    brand: brand || undefined,
    last4,
    label: provider === 'hipay' ? 'HiPay' : provider.charAt(0).toUpperCase() + provider.slice(1),
  };
}

function isMeaningful(value: string | null | undefined): value is string {
  return !!value && value !== '-';
}

/** `payments.metadata.paymentType` of the on-chain crypto rail (`CRYPTO_ONCHAIN_PAYMENT_TYPE` on the API). */
const CRYPTO_ONCHAIN_PAYMENT_TYPE = 'crypto_onchain';

type CryptoOnchainDisplay = Extract<PaymentMethodDisplay, { kind: 'crypto' }>;

/**
 * An on-chain crypto payment: from the list's `cryptoOnchain`, else from the metadata alone (a
 * table whose read does not join the mirror row), which knows the coin once paid but no network.
 */
function getCryptoOnchainDisplay(payment: PaymentMethodInput): CryptoOnchainDisplay | null {
  const crypto = payment.cryptoOnchain;
  if (crypto) {
    return {
      kind: 'crypto',
      asset: crypto.asset ?? undefined,
      network: crypto.network ?? undefined,
      isTest: crypto.isTest,
    };
  }
  const metadata = payment.metadata;
  if (metadata?.paymentType !== CRYPTO_ONCHAIN_PAYMENT_TYPE) return null;
  const asset = typeof metadata.asset === 'string' && metadata.asset.length > 0 ? metadata.asset : undefined;
  return { kind: 'crypto', asset, network: undefined, isTest: metadata.livemode === false };
}

/** The coin and the network a crypto payment was quoted on: `ETH (Ethereum Sepolia)`, `USDC (Base)`. */
export function getCryptoOnchainAssetLabel(display: { asset?: string; network?: string }): string | undefined {
  if (display.asset && display.network) return `${display.asset} (${display.network})`;
  return display.asset ?? display.network;
}

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Resolve a payment to a structured display intent. Pure, framework-free —
 * safe to use in tables, exports, audit logs, etc.
 */
export function getPaymentMethodDisplay(payment: PaymentMethodInput): PaymentMethodDisplay {
  // 0. On-chain crypto: no instrument, no card.
  const crypto = getCryptoOnchainDisplay(payment);
  if (crypto) return crypto;

  const instrument = payment.paymentInstrument;
  const card = instrument?.card;
  const cardBrand = isMeaningful(card?.brand ?? null) ? card!.brand! : undefined;
  const cardLast4 = isMeaningful(card?.last4 ?? null) ? card!.last4! : undefined;

  const typeFromInstrument = instrument?.type ?? undefined;
  const typeFromMetadata = (payment.metadata?.paymentMethod as string | undefined) ?? undefined;
  const effectiveType = typeFromInstrument ?? typeFromMetadata;

  // 1. Wallets (Apple Pay / Google Pay): wrap the underlying card. Covers both
  //    the card-wallet rows and the Stripe `apm_stripe_*` express rows.
  const wallet = resolveWalletType(effectiveType);
  if (wallet) {
    return {
      kind: 'wallet',
      wallet,
      label: WALLET_LABELS[wallet],
      brand: cardBrand,
      last4: cardLast4,
    };
  }

  // 2. APM (Klarna, PayPal, …) — by explicit map or `apm_*` prefix fallback.
  if (effectiveType && effectiveType !== 'card') {
    const mapped = APM_LABELS[effectiveType];
    if (mapped) return { kind: 'apm', label: mapped };
    if (effectiveType.startsWith('apm_')) {
      return {
        kind: 'apm',
        label: effectiveType.replace(/^apm_(?:airwallex|stripe)_/, '').replace(/_/g, ' '),
      };
    }
  }

  // 3. Real card data on the instrument.
  if (cardBrand || cardLast4) {
    return { kind: 'card', brand: cardBrand, last4: cardLast4 };
  }

  // 4. Integration-stored card (HiPay, Convesio, custom_payment).
  const integration = getIntegrationDisplay(payment.metadata);
  if (integration) {
    if (integration.brand || integration.last4) {
      return {
        kind: 'integration',
        label: integration.label,
        brand: integration.brand,
        last4: integration.last4,
      };
    }
    return { kind: 'integration', label: integration.label };
  }

  return { kind: 'unknown' };
}

// ---------------------------------------------------------------------------
// React cell
// ---------------------------------------------------------------------------

interface PaymentMethodCellProps {
  payment: PaymentMethodInput;
  className?: string;
  /** The table has its own Payment type column: a crypto payment shows no coin and network here. */
  isAssetHidden?: boolean;
}

/**
 * Renders the resolved payment method as it should appear inside a TanStack
 * Table cell. Wallets get a `Wallet · Brand •••• 1234` layout so it's
 * obvious at a glance which underlying card was charged.
 */
export function PaymentMethodCell({ payment, className, isAssetHidden = false }: PaymentMethodCellProps) {
  const display = getPaymentMethodDisplay(payment);

  switch (display.kind) {
    case 'crypto':
      return (
        <CryptoOnchainMethodBadge
          assetLabel={isAssetHidden ? undefined : getCryptoOnchainAssetLabel(display)}
          isTest={display.isTest}
          className={className}
        />
      );
    case 'wallet': {
      const hasUnderlyingCard = display.brand || display.last4;
      return (
        <div className={cn('flex items-center gap-2', className)}>
          <Badge variant="secondary" className="font-medium">
            {display.label}
          </Badge>
          {hasUnderlyingCard && <CardBadge brand={display.brand} last4={display.last4} />}
        </div>
      );
    }
    case 'apm':
      return (
        <Badge variant="secondary" className={cn('font-medium', className)}>
          {display.label}
        </Badge>
      );
    case 'card':
      return <CardBadge brand={display.brand} last4={display.last4} className={className} />;
    case 'integration': {
      const hasCard = display.brand || display.last4;
      if (hasCard) {
        return (
          <div className={cn('flex items-center gap-2', className)}>
            <Badge variant="outline" className="font-medium">
              {display.label}
            </Badge>
            <CardBadge brand={display.brand} last4={display.last4} />
          </div>
        );
      }
      return (
        <Badge variant="outline" className={cn('font-medium', className)}>
          {display.label}
        </Badge>
      );
    }
    case 'unknown':
    default:
      return <span className={cn('text-muted-foreground text-sm', className)}>—</span>;
  }
}
